import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaUser, FaCheck, FaBriefcase, FaStar } from 'react-icons/fa6';

/**
 * MINERVA REFACTORED - Comissões da Federação
 * Premium Design v3.0 | Chumbo + Dourado
 * Paleta de Marca | Efeito Sutil Luxuoso | Design Consistente
 * Last Updated: 2026-01-28
 */

interface CommissionMember {
  nome: string;
  especializacao: string;
  destaque?: string;
}

interface Commission {
  id: string;
  titulo: string;
  descricao: string;
  objetivo: string;
  membros: CommissionMember[];
  responsabilidades: string[];
}

const Comissoes: React.FC = () => {
  const [expandedCommission, setExpandedCommission] = useState<string | null>('avaliacao');
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-scroll-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const comissoes: Commission[] = [
    {
      id: 'avaliacao',
      titulo: 'Comissão de Avaliação Educacional',
      descricao:
        'Núcleo responsável pela análise técnica, pedagógica e ética dos profissionais que buscam filiação à Federação.',
      objetivo:
        'Guardião da qualidade e da legitimidade profissional em nosso setor, com Excelência Técnica e Julgamento Ético a Serviço da Profissão.',
      responsabilidades: [
        'Avaliar criteriosamente a formação técnica e prática dos profissionais',
        'Analisar documentação comprobatória, certificados e portfólios',
        'Realizar entrevistas, testes práticos ou exigências complementares',
        'Emitir pareceres técnicos sobre aptidão profissional',
        'Manter atualização contínua de normas técnicas e critérios de qualidade',
        'Apoiar na formulação de conteúdos formativos e diretrizes curriculares',
        'Promover ética, segurança e competência profissional',
      ],
      membros: [
        {
          nome: 'Jorge Pompeo Gassi',
          especializacao: 'Analista de Membros Honorários',
          destaque: 'Coordenador',
        },
        {
          nome: 'Aline Gassi',
          especializacao: 'Farmacêutica e Bioquímica, Especialista em Estética e Terapias Complementares',
        },
        {
          nome: 'Vânia Maria',
          especializacao: 'Mentora, Especialista em Educação na Área da Beleza',
        },
        {
          nome: 'Daiana Moraes',
          especializacao: 'Palestrante, Maquiadora e Especialista em Beleza Afro',
        },
        {
          nome: 'Edson França',
          especializacao: 'Graduado em Tricologia e Terapia Capilar, Pós-Graduado em Psicologia Clínica',
        },
        {
          nome: 'Meire Caetano',
          especializacao: 'Instrutora Master, Mentora, Especialista em Cutilagem e Saúde das Unhas',
        },
        {
          nome: 'Neto Campos',
          especializacao: 'Mentor e Instrutor Master em Cortes Femininos de Tendências Internacionais',
        },
        {
          nome: 'Alan Albuquerque',
          especializacao: 'Palestrante, Graduado em Química, Instrutor Master em Coloração',
        },
        {
          nome: 'Everton Borba',
          especializacao: 'Instrutor Master em Barbearia, Especialista em Corte Masculino',
        },
        {
          nome: 'Lucas Fernandes',
          especializacao: 'Instrutor Master em Estética Masculina e Gestão em Barbearia',
        },
        {
          nome: 'Fernando Monteiro',
          especializacao: 'Instrutor Master, Visagista e Especialista na Arte de Barbear',
        },
        {
          nome: 'Washington Costa',
          especializacao: 'Instrutor Master em Barbearia, Especialista em Corte Masculino',
        },
      ],
    },
    {
      id: 'desenvolvimento',
      titulo: 'Comissão Técnica de Desenvolvimento Social',
      descricao:
        'Responsável pela articulação institucional, projetos sociais e promoção de eventos que fortalecem a profissão.',
      objetivo: 'Ampliar o impacto social da Federação e fortalecer relações institucionais estratégicas.',
      responsabilidades: [
        'Articular parcerias com instituições públicas e privadas',
        'Coordenar projetos sociais e educacionais',
        'Produzir e organizar eventos de destaque profissional',
        'Promover inclusão social no setor da beleza',
        'Fortalecer relações institucionais em nível nacional e internacional',
      ],
      membros: [
        {
          nome: 'Erica Macedo',
          especializacao: 'Representante do Comitê Técnico de Articulação e Relações Institucionais',
          destaque: 'Coordenadora',
        },
        {
          nome: 'Jaqueline Antiquera',
          especializacao: 'Representante de Projetos Sociais e Produtora de Eventos',
        },
      ],
    },
  ];

  const composicaoEducacional = [
    'Professores licenciados e tecnólogos da beleza',
    'Coordenadores pedagógicos com vivência em ensino técnico e superior',
    'Especialistas de campo com reconhecimento nacional e internacional',
    'Consultores em regulamentação, biossegurança e certificações',
    'Profissionais com notório saber em sua área de atuação',
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        [data-scroll-animate] {
          opacity: 0;
          animation: fadeInUp 0.7s ease-out forwards;
        }

        .scroll-visible {
          opacity: 1 !important;
        }

        [data-delay="1"] { animation-delay: 0.1s; }
        [data-delay="2"] { animation-delay: 0.2s; }
        [data-delay="3"] { animation-delay: 0.3s; }
        [data-delay="4"] { animation-delay: 0.4s; }
        [data-delay="5"] { animation-delay: 0.5s; }
        [data-delay="6"] { animation-delay: 0.6s; }

        .commission-header {
          position: relative;
          overflow: hidden;
        }

        .commission-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(180, 83, 9, 0.04) 100%);
          pointer-events: none;
        }

        .commission-header::after {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(217, 119, 6, 0.1) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          pointer-events: none;
        }

        .member-item {
          animation: fadeInUp 0.5s ease-out;
        }

        .member-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: black;
          font-size: 18px;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.15);
        }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-20 pb-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              id="hero-title"
              data-scroll-animate
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight scroll-visible drop-shadow-lg"
            >
              Comissões Especializadas
            </h1>
            <p 
              id="hero-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg md:text-xl text-gray-100 leading-relaxed scroll-visible drop-shadow-md"
            >
              Estruturas especializadas que <span className="font-black text-amber-300">conduzem as principais áreas de atuação</span> da Federação com excelência técnica e compromisso ético.
            </p>
          </div>
        </div>
      </section>

      {/* COMPOSIÇÃO EDUCACIONAL */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 
              id="composition-title"
              data-scroll-animate
              className="text-3xl font-black text-gray-900 mb-8 scroll-visible"
            >
              Composição do Corpo Educacional
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {composicaoEducacional.map((item, idx) => (
                <div
                  key={idx}
                  id={`composition-${idx}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 1, 6))}
                  className="bg-white rounded-lg p-4 border-l-4 border-amber-500 shadow-sm scroll-visible hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-3">
                    <FaCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 font-black" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMISSÕES EXPANDÍVEIS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 
              id="commissions-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Detalhes das Comissões
            </h2>
            <p 
              id="commissions-desc"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-12 max-w-2xl mx-auto scroll-visible"
            >
              Clique em cada comissão para conhecer seus membros e responsabilidades
            </p>

            <div className="space-y-4">
              {comissoes.map((comissao, idx) => (
                <div 
                  key={comissao.id}
                  id={`commission-${comissao.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="bg-white rounded-xl overflow-hidden border-2 border-amber-200/60 shadow-lg scroll-visible"
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedCommission(expandedCommission === comissao.id ? null : comissao.id)}
                    className="commission-header w-full bg-gradient-to-r from-gray-800 to-gray-900 p-6 md:p-8 text-white flex items-center justify-between hover:opacity-95 transition-all relative"
                  >
                    <div className="text-left relative z-10">
                      <h3 className="text-xl md:text-2xl font-black">{comissao.titulo}</h3>
                      <p className="text-amber-200 text-sm mt-2 font-medium">
                        {comissao.membros.length} membros
                      </p>
                    </div>
                    <FaChevronDown
                      className={`w-6 h-6 transition-transform flex-shrink-0 relative z-10 ${
                        expandedCommission === comissao.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Content */}
                  {expandedCommission === comissao.id && (
                    <div className="p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white space-y-8">
                      {/* Descrição e Objetivo */}
                      <div>
                        <h4 className="font-black text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          Sobre a Comissão
                        </h4>
                        <p className="text-gray-700 leading-relaxed mb-4">{comissao.descricao}</p>
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-l-4 border-amber-500">
                          <p className="text-gray-800 font-medium">
                            <span className="font-black text-amber-600">🎯 </span>
                            {comissao.objetivo}
                          </p>
                        </div>
                      </div>

                      {/* Responsabilidades */}
                      <div>
                        <h4 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-wide">
                          Responsabilidades Principais
                        </h4>
                        <ul className="space-y-3">
                          {comissao.responsabilidades.map((resp, respIdx) => (
                            <li key={respIdx} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                                <span className="text-white text-xs font-black">✓</span>
                              </div>
                              <span className="text-gray-700 leading-relaxed">{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Membros */}
                      <div>
                        <h4 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-wide">
                          Membros da Comissão
                        </h4>
                        <div className="grid grid-cols-1 gap-4">
                          {comissao.membros.map((membro, membroIdx) => (
                            <div
                              key={membroIdx}
                              className="member-item p-4 bg-white rounded-lg border-2 border-amber-200/40 hover:border-amber-300 transition-colors"
                              style={{
                                animation: `fadeInUp 0.5s ease-out ${membroIdx * 50}ms both`,
                              }}
                            >
                              <div className="flex items-start gap-4">
                                <div className="member-avatar">
                                  {membro.nome
                                    .split(' ')
                                    .slice(0, 2)
                                    .map((n) => n[0])
                                    .join('')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <h5 className="font-black text-gray-900 text-sm">{membro.nome}</h5>
                                    {membro.destaque && (
                                      <span className="inline-block px-2 py-1 bg-gradient-to-r from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded text-xs font-black text-amber-700 flex-shrink-0">
                                        {membro.destaque}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 leading-relaxed">{membro.especializacao}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div 
            id="cta-card"
            data-scroll-animate
            className="max-w-2xl mx-auto text-center bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-amber-200 scroll-visible"
          >
            <h3 className="text-2xl font-black text-gray-900 mb-4">
              Gostaria de Participar?
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Enviamos um e-mail com seu CV destacando suas qualificações e interesse em participar de uma comissão.
            </p>
            <a
              href="mailto:comissoes@federacao.org.br"
              className="inline-block px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-black rounded-lg hover:shadow-lg transition-all"
            >
              Envie Seu Interesse
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Comissoes;